import { Save, Add, ExpandMore, ChevronRight } from '@mui/icons-material';
import { Grid, OutlinedInput, Paper } from '@mui/material';
import { yellow } from '@mui/material/colors';
import { equals, isEmpty, update } from 'ramda';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { CHECKPOINTS_TYPE, CHECKPOINT_GROUP } from '../../consts';
import messages from '../../intl/messages';
import { showProcessModal } from '../../store/modal/actions';
import { editCheckpoints, fetchCheckpoints, setProcessWasEdited } from '../../store/model/actions';
import { getConfigDefinitionId, getCheckpoints, getProcessWasEdited } from '../../store/model/selectors';
import { getCurrentLangCode } from '../../store/ui/selectors';
import Switch, { IOSSwitch } from '../Switch';
import UniButtton from '../UniButtton';

const useStyles = makeStyles()((theme) => ({
	bounding: {
		padding: '0.2rem 2rem 2rem 2rem',
		fontSize: '0.9rem',
		'&.draft': {
			background: yellow['100'],
		},
		'& h2': {
			display: 'inline-block',
		},
		'& .buttonsContainer': { float: 'right', marginTop: '7px', display: 'flex' },
		'& span.saveIcon': { margin: '19px' },
		'& .header': {
			fontWeight: 'bold',
			fontSize: '0.8rem',
			textAlign: 'center',
			padding: '10px 0',
		},
		'& .clickable': {
			cursor: 'pointer',
		},
		// 12 columns with a 16px gap give the same column widths as the former MUI Grid container with spacing={2}
		'& .cpGrid': {
			display: 'grid',
			gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
			columnGap: '16px',
			alignItems: 'center',
		},
		'& .span1': { gridColumn: 'span 1' },
		'& .span2': { gridColumn: 'span 2' },
		'& .span10': { gridColumn: 'span 10' },
		'& .cpInner': {
			display: 'grid',
			gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
			columnGap: '16px',
			alignItems: 'center',
		},
		'& .cpGrid.hiddenRow': { display: 'none' },
		'& .row': {
			textAlign: 'center',
			'& .alignCenter': {
				textAlign: 'center',
				'& input': {
					textAlign: 'center',
				},
			},
			'& .checkpointTypeColumn': {
				position: 'relative',
			},
		},
		'& .toggle': {
			cursor: 'pointer',
		},
	},
}));

// Sort table by tab and evaluationGroup, alphabetically descending
const comparator = (a, b) => {
	const isProcessAnalysis = (checkpointGroup) => checkpointGroup === CHECKPOINT_GROUP.PROCESS_ANALYSIS;

	if (isProcessAnalysis(a.checkpointGroup) && !isProcessAnalysis(b.checkpointGroup)) return -1;
	else if (isProcessAnalysis(b.checkpointGroup) && !isProcessAnalysis(a.checkpointGroup)) return 1;

	if (a.tab < b.tab) return -1;
	else if (a.tab === b.tab) {
		if (a.evaluationGroup < b.evaluationGroup) return -1;
		else return 1;
	} else return 1;
};

const isProcessCheckpoint = (checkpoint) =>
	checkpoint.checkpointType === CHECKPOINTS_TYPE.PROCESS &&
	checkpoint.checkpointGroup === CHECKPOINT_GROUP.PROCESS_ANALYSIS;

// Memoized: only the row whose checkpoint changed re-renders, so props must stay referentially stable
const CheckpointRow = memo(({ checkpoint, index, hidden, onFieldChange, onOpenProcess }) => {
	const {
		checkpointGroup,
		checkpointType,
		confidenceThreshold,
		confidenceThresholdLow,
		displayed,
		enabled,
		evaluationGroup,
		frameOccurence,
		name,
		streamType,
		violationMessage,
		tab,
	} = checkpoint;
	const isProcessAnalysis = checkpointGroup === CHECKPOINT_GROUP.PROCESS_ANALYSIS;
	const stopPropagation = (e) => e.stopPropagation();

	return (
		<div
			className={`cpGrid row ${isProcessAnalysis ? 'clickable' : ''} ${hidden ? 'hiddenRow' : ''}`}
			// This requires click stop handlers in input fields
			onClick={isProcessAnalysis ? () => onOpenProcess(checkpoint) : undefined}
		>
			<div className="span1 checkpointTypeColumn">{checkpointType}</div>
			<div className="span1 checkpointTypeColumn">{tab}</div>
			<div className="span1">{streamType}</div>
			<div className="span1">{checkpointGroup}</div>
			<div className="span1">{evaluationGroup}</div>
			<div className="span1">
				<div>{name}</div>
			</div>
			<div className="span2 cpInner">
				{/* Each InputBase otherwise injects its own autofill keyframes; with ~100 rows that is thousands of <style> tags */}
				<OutlinedInput
					disableInjectingGlobalStyles
					value={confidenceThreshold}
					onChange={(e) => onFieldChange(index, 'confidenceThreshold', Number(e.target.value))}
					onClick={stopPropagation}
					size="small"
					className="alignCenter"
				/>
				<OutlinedInput
					disableInjectingGlobalStyles
					value={confidenceThresholdLow}
					onChange={(e) => onFieldChange(index, 'confidenceThresholdLow', Number(e.target.value))}
					onClick={stopPropagation}
					size="small"
					className="alignCenter"
				/>
				<OutlinedInput
					disableInjectingGlobalStyles
					value={frameOccurence}
					onChange={(e) => onFieldChange(index, 'frameOccurence', Number(e.target.value))}
					onClick={stopPropagation}
					size="small"
					className="alignCenter"
				/>
			</div>
			<div className="span2">
				<OutlinedInput
					disableInjectingGlobalStyles
					value={violationMessage}
					onChange={(e) => onFieldChange(index, 'violationMessage', e.target.value)}
					onClick={stopPropagation}
					size="small"
					fullWidth
				/>
			</div>
			{/* Switches don't have a click handler... */}
			<div className="span1" onClick={stopPropagation}>
				<IOSSwitch checked={displayed} onChange={() => onFieldChange(index, 'displayed', !displayed)} />
			</div>
			<div className="span1" onClick={stopPropagation}>
				<IOSSwitch checked={enabled} onChange={() => onFieldChange(index, 'enabled', !enabled)} />
			</div>
		</div>
	);
});

const isAllOn = (checkpoints, prop) => {
	for (const checkpoint of checkpoints) {
		if (!checkpoint[prop] && isProcessCheckpoint(checkpoint)) return false;
	}
	return true;
};

const setAllTo = (checkpoints, prop, value) =>
	checkpoints.map((checkpoint) => (isProcessCheckpoint(checkpoint) ? { ...checkpoint, [prop]: value } : checkpoint));

// True when every incoming checkpoint has an equal counterpart (by id) in the local state
const isSubsetOfState = (checkpoints, state) => {
	const stateById = new Map(state.map((checkpoint) => [checkpoint.id, checkpoint]));

	return checkpoints.every((checkpoint) => {
		const stateCheckpoint = stateById.get(checkpoint.id);

		return stateCheckpoint !== undefined && (stateCheckpoint === checkpoint || equals(checkpoint, stateCheckpoint));
	});
};

const Checkpoints = () => {
	const { classes } = useStyles();
	const definitionId = useSelector(getConfigDefinitionId);
	const checkpoints = useSelector(getCheckpoints(definitionId));
	const [state, setState] = useState(checkpoints.sort(comparator));
	const [isProcessTypeVisible, setIsProcessTypeVisible] = useState(false);
	// Process rows stay mounted once expanded, collapsing only hides them
	const [wereProcessesExpanded, setWereProcessesExpanded] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const dispatch = useDispatch();
	const [isDraft, setIsDraft] = useState(false);
	const currentLangCode = useSelector(getCurrentLangCode);
	const processWasEdited = useSelector(getProcessWasEdited);
	const areAllProcessTypesDisplayed = useMemo(() => isAllOn(state, 'displayed'), [state]);
	const areAllProcessTypesEnabled = useMemo(() => isAllOn(state, 'enabled'), [state]);

	const displayedAll = state.every((checkpoint) => checkpoint.displayed);
	const enabledAll = state.every((checkpoint) => checkpoint.enabled);

	const hasProcessRows = state.some(isProcessCheckpoint);

	const setProcessesVisible = (visible) => {
		setIsProcessTypeVisible(visible);
		if (visible) setWereProcessesExpanded(true);
	};

	const handleFieldChange = useCallback(
		(index, field, value) => setState((prev) => update(index, { ...prev[index], [field]: value }, prev)),
		[]
	);
	const handleOpenProcess = useCallback((checkpoint) => dispatch(showProcessModal(checkpoint)), [dispatch]);

	useEffect(() => {
		(async () => {
			await dispatch(fetchCheckpoints(definitionId));
			setState([]);
		})();
	}, [currentLangCode, definitionId, dispatch]);

	useEffect(() => {
		if (!isEmpty(checkpoints)) {
			if (isEmpty(state)) setState(checkpoints);

			if (isSubsetOfState(checkpoints, state)) {
				window.onbeforeunload = () => {};
				setIsDraft(false);
				// In case new checkpoints (processes) were created/deleted
				// A special flag has to be raised when a process is edited using the dialog
			} else if (state.length !== checkpoints.length || processWasEdited) {
				setState(checkpoints);
				dispatch(setProcessWasEdited(false));
			} else if (!isDraft && !isEmpty(state)) {
				window.onbeforeunload = () => false;
				setIsDraft(true);
			}
		}
	}, [checkpoints, dispatch, isDraft, processWasEdited, state]);

	const handleSave = async () => {
		setIsSaving(true);
		await dispatch(editCheckpoints(state, definitionId));
		setIsSaving(false);
		window.onbeforeunload = () => {};
	};

	// Rendered even without process rows, otherwise a definition with no processes could not get its first one
	const renderProcessHeader = (hasRows) => (
		<Grid
			container
			spacing={2}
			onClick={hasRows ? () => setProcessesVisible(!isProcessTypeVisible) : undefined}
			alignItems="center"
			className={hasRows ? 'toggle' : undefined}
		>
			<Grid item alignItems="center" xs={10}>
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{hasRows && (isProcessTypeVisible ? <ExpandMore /> : <ChevronRight />)}
					<FormattedMessage {...messages.processes} />
					<UniButtton
						tooltip={<FormattedMessage {...messages.addProcess} />}
						color="primary"
						onClick={(e) => {
							e.stopPropagation();
							dispatch(showProcessModal());
						}}
						icon={<Add fontSize="large" />}
						disabled={isSaving}
					/>
				</div>
			</Grid>
			{hasRows && (
				<>
					<Grid item alignItems="center" xs={1} onClick={(e) => e.stopPropagation()}>
						<Switch
							checked={areAllProcessTypesDisplayed}
							handleChange={() => {
								setProcessesVisible(true);
								setState(setAllTo(state, 'displayed', !areAllProcessTypesDisplayed));
							}}
							labelPlacement="top"
						/>
					</Grid>
					<Grid item alignItems="center" xs={1} onClick={(e) => e.stopPropagation()}>
						<Switch
							checked={areAllProcessTypesEnabled}
							handleChange={() => {
								setProcessesVisible(true);
								setState(setAllTo(state, 'enabled', !areAllProcessTypesEnabled));
							}}
							labelPlacement="top"
						/>
					</Grid>
				</>
			)}
		</Grid>
	);

	return (
		<Paper elevation={3} className={`${classes.bounding} ${isDraft && 'draft'}`}>
			<h2>
				<FormattedMessage {...messages.checkpoints} />
			</h2>
			<div className="buttonsContainer">
				{isSaving ? (
					<span className="saveIcon">
						<FormattedMessage {...messages.uploading} /> ...
					</span>
				) : (
					<UniButtton
						tooltip={<FormattedMessage {...messages[isDraft ? 'saveAllCheckpoints' : 'noChanges']} />}
						color="primary"
						onClick={handleSave}
						icon={<Save fontSize="large" />}
						disabled={!isDraft}
					/>
				)}
			</div>
			<div className="cpGrid header">
				<div className="span1">
					<FormattedMessage {...messages.type} />
				</div>
				<div className="span1">
					<FormattedMessage {...messages.panel} />
				</div>
				<div className="span1">
					<FormattedMessage {...messages.streamType} />
				</div>
				<div className="span1">
					<FormattedMessage {...messages.checkpointGroup} />
				</div>
				<div className="span1">
					<FormattedMessage {...messages.evaluationGroup} />
				</div>
				<div className="span1">
					<FormattedMessage {...messages.name} />
				</div>
				<div className="span2 cpInner">
					<div>
						<FormattedMessage {...messages.confidanceTreshold} />
					</div>
					<div>
						<FormattedMessage {...messages.confidanceTresholdLow} />
					</div>
					<div>
						<FormattedMessage {...messages.frameOccurance} />
					</div>
				</div>
				<div className="span2">
					<FormattedMessage {...messages.violationMessage} />
				</div>
				<div className="span1">
					<FormattedMessage {...messages.display} />
				</div>
				<div className="span1">
					<FormattedMessage {...messages.enabled} />
				</div>
			</div>
			<Grid container spacing={2}>
				<Grid item xs={10}></Grid>
				{/* Switches don't have a click handler... */}
				<Grid item xs={1} onClick={(e) => e.stopPropagation()}>
					<Switch
						checked={displayedAll}
						handleChange={(e) => setState(state.map((checkpoint) => ({ ...checkpoint, displayed: !displayedAll })))}
						labelPlacement="top"
					/>
				</Grid>
				<Grid item xs={1} onClick={(e) => e.stopPropagation()}>
					<Switch
						checked={enabledAll}
						handleChange={(e) => setState(state.map((checkpoint) => ({ ...checkpoint, enabled: !enabledAll })))}
						labelPlacement="top"
					/>
				</Grid>
			</Grid>

			{!isEmpty(state) && !hasProcessRows && renderProcessHeader(false)}

			{state.map((checkpoint, index) => {
				const isProcessType = isProcessCheckpoint(checkpoint);
				const isFirstProcessType = isProcessType && (index === 0 || !isProcessCheckpoint(state[index - 1]));
				const row =
					!isProcessType || wereProcessesExpanded ? (
						<CheckpointRow
							checkpoint={checkpoint}
							index={index}
							hidden={isProcessType && !isProcessTypeVisible}
							onFieldChange={handleFieldChange}
							onOpenProcess={handleOpenProcess}
						/>
					) : null;

				return (
					<React.Fragment key={checkpoint.id}>
						{isFirstProcessType && renderProcessHeader(true)}
						{row}
					</React.Fragment>
				);
			})}
		</Paper>
	);
};

export default Checkpoints;
